import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import ProductFormButton from '../product_detail/components/ProductFormButton';
import { ProductControlProps } from './ProductControls';
import { GiftCardDenominationSwatches } from './GiftCardDenominationSwatches';
import { useProductSelection } from '../hooks/useProductSelection';
import { useRecoilValue } from 'recoil';
import { environmentContextState } from '../recoil/atoms';
import { Button } from '../root/components/buttons/Button';
import styles from './GiftCardControlsMobile.module.scss';

declare const user_picked_slug: string | undefined;
declare const sktAsyncLoad: () => void;

// eslint-disable-next-line max-lines-per-function
export const GiftCardControlsMobile: React.FC<ProductControlProps> = ({ selectionType, style, selectionKey }) => {
  const { denomination, setDenomination, product } = useProductSelection(selectionType, selectionKey);
  const environmentContext = useRecoilValue(environmentContextState);
  const [propertiesTo, setPropertiesTo] = useState<string>('');
  const [propertiesFrom, setPropertiesFrom] = useState<string>('');
  const [propertiesMessage, setPropertiesMessage] = useState<string>('');
  const [propertiesCustomization, setPropertiesCustomization] = useState<string>('');
  const [isCustomizationComplete, setIsCustomizationComplete] = useState<boolean>(false);
  const customizeRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const customizationRef = useRef<HTMLInputElement>(null);
  const shopName = environmentContext.permanentDomain.includes('.') ? environmentContext.permanentDomain.split('.')[0] : 'honeylove-dev';
  const shopId = environmentContext.giftCardShopId;
  const [selectedDesign, setSelectedDesign] = useState<string | undefined>();

  React.useEffect(() => {
    sktAsyncLoad();

    if (!denomination) {
      setDenomination(product.denominations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateSelectedDesign = (fromDefault?: string, messageDefault?: string) => {
      const customization = (document.getElementById('skt_gc_slug') as HTMLInputElement)?.value ?? '';
      const to = (document.getElementById('skt_gc_to') as HTMLInputElement)?.value ?? '';
      const toCustomization = (document.getElementById('skt_gc_customize_to_name') as HTMLInputElement)?.value ?? '';
      const from = (document.getElementById('skt_gc_customize_from_name') as HTMLInputElement)?.value ?? fromDefault ?? '';
      const message = (document.getElementById('skt_gc_customize_message') as HTMLInputElement)?.value ?? messageDefault ?? '';

      setPropertiesTo(toCustomization);
      setPropertiesFrom(from);
      setPropertiesMessage(message);

      if (user_picked_slug && customization) {
        setSelectedDesign(user_picked_slug);
        setIsCustomizationComplete(true);
        setPropertiesTo(to);
        setPropertiesCustomization(customization);

        // This timeout is really gross, but I don't know
        // a better way to handle an update of the gift card
        // design when there's already a selected design
        setTimeout(() => {
          setPropertiesTo((document.getElementById('skt_gc_to') as HTMLInputElement)?.value);
          setPropertiesCustomization((document.getElementById('skt_gc_slug') as HTMLInputElement)?.value);
        }, 500);
      } else {
        setTimeout(() => updateSelectedDesign(from, message), 500);
      }
    };

    const disableAutoComplete = () => {
      const giftCardForm = document.querySelector('#skt-gc-page form');
      const fromInput = document.getElementById('skt_gc_customize_from_name');
      const toInput = document.getElementById('skt_gc_customize_to_name');

      fromInput?.setAttribute?.('autocomplete', 'off');
      toInput?.setAttribute?.('autocomplete', 'off');
      giftCardForm?.setAttribute?.('autocomplete', 'off');
    };

    const attachSaveListener = () => {
      const saveButton = document.querySelector('.skt-gc-save');
      if (saveButton) {
        saveButton.addEventListener('click', () => {
          updateSelectedDesign();
        });
        disableAutoComplete();
      } else {
        setTimeout(() => attachSaveListener(), 500);
      }
    };

    const clearDesign = () => {
      setIsCustomizationComplete(false);
      setPropertiesCustomization('');
      setPropertiesTo('');
      setPropertiesFrom('');
      setPropertiesMessage('');
      setSelectedDesign(undefined);
      const to = document.getElementById('skt_gc_to') as HTMLInputElement;
      if (to) {
        to.value = '';
      }
      const slug = document.getElementById('skt_gc_slug') as HTMLInputElement;
      if (slug) {
        slug.value = '';
      }
    };

    const attachCustomizeListener = () => {
      const customize = document.querySelector('.skt_gc_button_container');
      const preview = document.querySelector('.skt_gc_preview_button');
      if (customize && preview) {
        customize.addEventListener('click', () => {
          // The DOM gets torn apart when the modal closes, so we need to re-attach the save listener on every click
          clearDesign();
          attachSaveListener();
        });

        preview?.classList?.add('href-disabled');
      } else {
        setTimeout(() => attachCustomizeListener(), 500);
      }
    };

    attachCustomizeListener();
  }, []);

  useEffect(() => {
    if (isCustomizationComplete) {
      const preview = document.querySelector('.skt_gc_preview_button');
      preview?.classList?.remove('href-disabled');
    } else {
      const preview = document.querySelector('.skt_gc_preview_button');
      preview?.classList?.add('href-disabled');
    }
  }, [isCustomizationComplete]);

  const Customize = () => (
    <Button
      onClick={() => customizeRef?.current?.querySelector('a')?.click()}
      disabled={!selectedDesign}
      text="Customize"
      buttonType="link"
      noMargin={true}
      labelStyleOptions={{ underline: true }}
      size="small"
    />
  );

  const Preview = () => (
    <Button
      onClick={() => previewRef?.current?.querySelector('a')?.click()}
      text="Preview"
      noMargin={true}
      buttonType="link"
      labelStyleOptions={{ underline: true }}
      size="small"
    />
  );

  return (
    <div style={style}>
      <div className={styles.controlsWrapper}>
        <div ref={customizeRef} style={{ display: 'none' }} className="skt_gc_button_container"></div>
        <div ref={previewRef} style={{ display: 'none' }} className="skt_gc_preview_button_container"></div>
        <div className={styles.giftCardDetails}>
          <span className={styles.title}>To:</span>
          <span className={styles.content}>{propertiesTo}</span>
          <Customize />
        </div>
        <div className={styles.giftCardDetails}>
          <span className={styles.title}>From:</span>
          <span className={styles.content}>{propertiesFrom}</span>
          <Customize />
        </div>
        <div className={styles.giftCardDetails}>
          <span className={styles.title}>Message:</span>
          <span className={styles.content}>{propertiesMessage}</span>
          <Customize />
        </div>
        <Preview />
        <GiftCardDenominationSwatches product={product} selectionType={selectionType} selectionKey={selectionKey} style={{ margin: '10px 0' }} />
        <ProductFormButton
          selectionType={selectionType}
          selectionKey={selectionKey}
          quantity={1}
          properties={{ Customization: propertiesCustomization, To: propertiesTo }}
        >
          <div id="skt_gc_line_items">
            <input ref={customizationRef} type="hidden" id="skt_gc_slug" name="properties[Customization]" value={propertiesCustomization} disabled={true} />
            <input ref={toRef} type="hidden" id="skt_gc_to" name="properties[To]" value={propertiesTo} disabled={true} />
            <input type="hidden" id="skt_gc_shop_id" value={shopId} disabled={true} />
            <input type="hidden" id="skt_gc_shop_name" value={shopName} disabled={true} />
          </div>
        </ProductFormButton>
      </div>
    </div>
  );
};

export default GiftCardControlsMobile;
